import { useCallback, useEffect } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import type { RootState } from "single-spa/store/types";

import advancedSearchFilteringTrackingService from "../services/tracking";
import { setExpandedFilterName } from "../store/action-creators";
import { selectExpandedFilter, selectSearchQuery } from "../store/selectors";
import type { SearchFilterInstance } from "../types/searchFilterInstance";

type HookParams<TFilter extends SearchFilterInstance = SearchFilterInstance, TKey extends string = string> = {
    readonly filterList: readonly TFilter[];
    readonly isSearchActive: boolean;
    readonly autoExpandFilterKey: TKey | null | undefined;
    readonly activeTab?: string | null;
    /** While the panel or config is still settling, a target card may not be in the list yet — do not collapse. */
    readonly deferCollapseWhileSettling?: boolean;
};

type HookValue<TKey extends string = string> = {
    expandedFilterKey: TKey | null;
    onFilterExpand: (filterKey: TKey | null) => void;
    onCollapseAllFilters: VoidFunction;
};

export const useFilterExpansion = <
    TFilter extends SearchFilterInstance = SearchFilterInstance,
    TKey extends string = string,
>(
    options?: HookParams<TFilter, TKey>,
): HookValue<TKey> => {
    const dispatch = useDispatch();

    const expandedFilterKey = useSelector(selectExpandedFilter) as TKey;
    const store = useStore<RootState>();

    const onCollapseAllFilters = useCallback(() => {
        dispatch(setExpandedFilterName(null));
    }, []);

    const onFilterExpand = useCallback(
        (filterName: TKey | null) => {
            const activeTab = options?.activeTab ?? null;

            if (filterName) {
                const currentQuery = selectSearchQuery(store.getState());

                advancedSearchFilteringTrackingService.filtersPanel.trackFilterExpand(
                    filterName,
                    activeTab,
                    currentQuery,
                );
            } else if (expandedFilterKey) {
                advancedSearchFilteringTrackingService.filtersPanel.trackFilterCollapse(expandedFilterKey, activeTab);
            }

            dispatch(setExpandedFilterName(filterName));
        },
        [options?.activeTab, expandedFilterKey, store],
    );

    useEffect(() => {
        const { isSearchActive, autoExpandFilterKey } = options ?? {};

        if (isSearchActive && autoExpandFilterKey) {
            dispatch(setExpandedFilterName(autoExpandFilterKey));
        }
    }, [options?.isSearchActive, options?.autoExpandFilterKey]);

    useEffect(() => {
        if (options?.deferCollapseWhileSettling) {
            return;
        }

        const isExpandedVisible = options?.filterList.some((item) => item.key === expandedFilterKey);

        // Collapse expanded filter if it's no longer visible
        if (!isExpandedVisible && expandedFilterKey) {
            onFilterExpand(null);
        }
    }, [options?.filterList, options?.deferCollapseWhileSettling, expandedFilterKey, onFilterExpand]);

    return {
        expandedFilterKey,
        onFilterExpand,
        onCollapseAllFilters,
    };
};
